import React from 'react';
import useStyles from './styles';
import { Avatar, Media } from '@elevator/ui';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as IconMDI from 'mdi-material-ui';
import { ROUTES } from 'config/Nav';
import clsx from 'clsx';
import { useRouter } from 'next/router';

/**
 * CreditChip Component
 *
 * Displays a credit entry as a chip with avatar, name, and credit type.
 * Supports two variants: 'view' (read-only, navigable) and 'edit' (editable with remove button).
 *
 * In 'view' mode:
 * - Clickable if the credit has a preferredUsername (navigates to user profile)
 * - Non-clickable for non-user credits
 *
 * In 'edit' mode:
 * - Clickable to enter edit mode (triggers editCredit callback)
 * - Displays active state styling when isActive is true
 * - Shows a remove button (X icon) to delete the credit
 *
 * @param props - Component props
 * @param props.variant - Chip variant: 'view' (read-only) or 'edit' (editable)
 * @param props.name - Display name of the credit
 * @param props.preferredUsername - Optional username for user credits (enables navigation)
 * @param props.avatar - Media object containing avatar image
 * @param props.type - Credit type label (e.g., "Artist", "Producer")
 * @param props.isActive - Whether the chip is in active/edit state (edit variant only)
 * @param props.index - Index of the credit in the list (edit variant only)
 * @param props.editCredit - Callback function to enter edit mode (edit variant only)
 * @param props.removeCredit - Callback function to remove the credit (edit variant only)
 * @param props.setEditIndex - Callback function to set the edit index (edit variant only)
 */
const CreditChip = <T extends ChipVariant>(props: CreditChipProps<T>) => {
  const { classes } = useStyles();
  const router = useRouter();

  const {
    variant,
    name,
    preferredUsername,
    avatar,
    type,
    index,
    isActive,
    editCredit,
    removeCredit,
    setEditIndex,
  } = props;

  const isUser = Boolean(preferredUsername);

  /**
   * Handles click events on the chip
   */
  const handleClick = () => {
    if (variant === 'edit' && editCredit && index !== undefined) {
      editCredit(index);
    } else if (variant === 'view' && isUser && preferredUsername) {
      router.push(ROUTES.USER.to + preferredUsername);
    }
  };

  /**
   * Handles remove button click (edit variant only)
   */
  const handleRemoveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (removeCredit) {
      removeCredit();
    }
    if (setEditIndex) {
      setEditIndex(-1);
    }
  };

  return (
    <Paper
      data-component="CreditChip"
      elevation={1}
      className={clsx(
        classes.root,
        variant === 'edit' && isActive && classes.active,
        (variant === 'edit' || (variant === 'view' && isUser)) && classes.clickable,
      )}
      onClick={
        variant === 'edit' || (variant === 'view' && isUser) ? handleClick : undefined
      }
    >
      <Avatar
        username={name}
        size="medium"
        src={avatar?.sourceUrl}
        srcSet={avatar?.sourceSet}
        alt={name}
        variant="square"
        className={classes.avatar}
      />
      <Stack className={classes.creditInfo} direction="column" justifyContent="center">
        <Typography variant="caption" color="textSecondary">
          {type}
        </Typography>
        <Typography variant="subtitle1" color="textPrimary">
          {name}
        </Typography>
      </Stack>
      {variant === 'edit' && (
        <IconButton onClick={handleRemoveClick} size="large">
          <IconMDI.TrashCan />
        </IconButton>
      )}
    </Paper>
  );
};

/**
 * Chip variant type
 */
export type ChipVariant = 'view' | 'edit';

/**
 * Props for the CreditChip component
 */
export interface CreditChipProps<T extends ChipVariant> {
  /** Chip variant: 'view' (read-only) or 'edit' (editable) */
  variant: ChipVariant;
  /** Display name of the credit */
  name: string;
  /** Optional username for user credits (enables navigation in view mode) */
  preferredUsername?: string;
  /** Media object containing avatar image */
  avatar: Media;
  /** Credit type label (e.g., "Artist", "Producer") */
  type: string;
  /** Whether the chip is in active/edit state (edit variant only) */
  isActive?: T extends 'edit' ? boolean : never;
  /** Index of the credit in the list (edit variant only) */
  index?: T extends 'edit' ? number : never;
  /** Callback function to enter edit mode (edit variant only) */
  editCredit?: T extends 'edit' ? (index: number) => void : never;
  /** Callback function to remove the credit (edit variant only) */
  removeCredit?: T extends 'edit' ? () => void : never;
  /** Callback function to set the edit index (edit variant only) */
  setEditIndex?: T extends 'edit' ? (index: number) => void : never;
}

export default CreditChip;
